import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './apiClient';

// ---------------------------------------------------------------------------
// Types (mirror the API's admin contracts)
// ---------------------------------------------------------------------------
export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  jobTitle?: string | null;
  departmentCode?: string | null;
  isActive: boolean;
  emailConfirmed: boolean;
  mustChangePassword: boolean;
  lastLoginDate?: string | null;
  roles: string[];
  /** A sign-in PIN is set on the account (by the person or predefined by an administrator). */
  mfaPinSet: boolean;
  authenticatorEnabled: boolean;
  /** Time-limited access (AD-04): the account stops signing in after this. */
  accessExpiresAt?: string | null;
  /** Locked after too many wrong passwords until this moment; an administrator may unlock sooner. */
  lockedUntil?: string | null;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface AdminRole {
  id: string;
  name: string;
  description?: string | null;
  isSystemRole: boolean;
  userCount: number;
  permissions: string[];
}

export interface PermissionItem {
  name: string;
  module: string;
  description: string;
}

export interface PermissionGroup {
  module: string;
  permissions: PermissionItem[];
}

export interface CreateUserInput {
  email: string;
  password: string;
  fullName: string;
  jobTitle?: string | null;
  departmentCode?: string | null;
  roles?: string[];
  emailConfirmed?: boolean;
  mustChangePassword?: boolean;
  /** A predefined sign-in PIN (4–8 digits) as the account's second factor. */
  mfaPin?: string | null;
  accessExpiresAt?: string | null;
}

export interface UpdateUserInput {
  fullName: string;
  jobTitle?: string | null;
  departmentCode?: string | null;
  accessExpiresAt?: string | null;
  clearAccessExpiry?: boolean;
}

/** Extracts the API's `{ error }` message from an Axios error. */
export function apiError(err: unknown): string {
  const anyErr = err as { response?: { data?: unknown }; message?: string };
  const data = anyErr?.response?.data as
    | { error?: string; detail?: string; title?: string; errors?: Record<string, string[]> }
    | undefined;
  if (data) {
    if (typeof data.error === 'string') return data.error;
    if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
    if (data.errors && typeof data.errors === 'object') {
      const first = Object.values(data.errors).flat().find((m) => typeof m === 'string');
      if (first) return first;
    }
    if (typeof data.title === 'string' && data.title.trim()) return data.title;
  }
  return anyErr?.message ?? 'Request failed.';
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------
export function useUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => (await apiClient.get<AdminUser[]>('/api/v1/users')).data,
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: async () => (await apiClient.get<AdminRole[]>('/api/v1/roles')).data,
  });
}

export function usePermissionCatalog() {
  return useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: async () => (await apiClient.get<PermissionGroup[]>('/api/v1/permissions')).data,
    staleTime: 10 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateUserInput) => (await apiClient.post<AdminUser>('/api/v1/users', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateUserInput }) =>
      (await apiClient.put<AdminUser>(`/api/v1/users/${id}`, input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useUpdateUserRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, roles }: { id: string; roles: string[] }) =>
      (await apiClient.put<AdminUser>(`/api/v1/users/${id}/roles`, { roles })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
  });
}

export function useUnlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.post<AdminUser>(`/api/v1/users/${id}/unlock`, {})).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await apiClient.put<AdminUser>(`/api/v1/users/${id}/active`, { isActive })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useUpdateRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, permissions }: { id: string; permissions: string[] }) =>
      (await apiClient.put<AdminRole>(`/api/v1/roles/${id}/permissions`, { permissions })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'roles'] }),
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string | null }) =>
      (await apiClient.post<AdminRole>('/api/v1/roles', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'roles'] }),
  });
}

export interface AdminResetPasswordResult {
  message: string;
  temporaryPassword?: string | null;
}

/** Admin-initiated password reset: sets a new temp password, emails it, forces change on next login. */
export function useResetUserPassword() {
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post<AdminResetPasswordResult>(`/api/v1/users/${id}/reset-password`, {})).data,
  });
}

export interface AdminSetPinResult {
  message: string;
  /** The PIN, returned only in Development; otherwise it went to the person by email. */
  pin?: string | null;
}

/** Predefines or resets a user's sign-in PIN (blank makes one up); it is emailed to them. */
export function useSetUserPin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, pin }: { id: string; pin?: string | null }) =>
      (await apiClient.post<AdminSetPinResult>(`/api/v1/users/${id}/pin`, { pin: pin || null })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useClearUserPin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.delete<AdminUser>(`/api/v1/users/${id}/pin`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

// ---------------------------------------------------------------------------
// Generic reference-data CRUD (Departments, Branches, Business Units, Document
// Categories, Settings). Each shares the same paged/filtered contract.
// ---------------------------------------------------------------------------
export interface ReferenceQuery {
  page: number;
  pageSize: number;
  search?: string;
  isActive?: boolean;
  category?: string;
}

/** Active positions mapped to dropdown options keyed by title (the value stored on records). */
export function usePositionOptions() {
  const { data } = useReferenceList<{ id: string; code: string; title: string; isActive: boolean }>(
    'positions',
    { page: 1, pageSize: 200, isActive: true },
  );
  return (data?.items ?? []).map((p) => ({ value: p.title, label: p.title }));
}

export function useReferenceList<T>(resource: string, params: ReferenceQuery) {
  return useQuery({
    queryKey: ['admin', resource, params],
    queryFn: async () => {
      const query: Record<string, string> = {
        page: String(params.page),
        pageSize: String(params.pageSize),
      };
      if (params.search) query.search = params.search;
      if (params.isActive !== undefined) query.isActive = String(params.isActive);
      if (params.category) query.category = params.category;
      return (await apiClient.get<PagedResult<T>>(`/api/v1/${resource}`, { params: query })).data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useReferenceMutations<T>(resource: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', resource] });

  const create = useMutation({
    mutationFn: async (input: Partial<T>) => (await apiClient.post<T>(`/api/v1/${resource}`, input)).data,
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<T> }) =>
      (await apiClient.put<T>(`/api/v1/${resource}/${id}`, input)).data,
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/${resource}/${id}`);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
}

// ---------------------------------------------------------------------------
// Email delivery — read-only view of the API's Email configuration plus a test send. The settings
// themselves live in the API's configuration (they include a credential), not in the settings table.
// ---------------------------------------------------------------------------
export interface EmailDeliveryInfo {
  provider: 'Log' | 'Smtp';
  deliversEmail: boolean;
  host: string | null;
  port: number | null;
  security: string | null;
  fromAddress: string | null;
  fromName: string | null;
  hasCredentials: boolean;
  missing: string[];
}

export interface EmailTestResult {
  sent: boolean;
  provider: string;
  to: string;
  error: string | null;
}

export function useEmailDelivery() {
  return useQuery({
    queryKey: ['admin', 'email-delivery'],
    queryFn: async () => (await apiClient.get<EmailDeliveryInfo>('/api/v1/settings/email')).data,
  });
}

export function useSendTestEmail() {
  return useMutation({
    /** Sends to `to` when given, otherwise to the signed-in administrator. */
    mutationFn: async (to?: string) =>
      (await apiClient.post<EmailTestResult>('/api/v1/settings/email/test', { to: to?.trim() || null })).data,
  });
}


// ---- the spreadsheet import of people (ToR 4.1.1)

export interface UserImportLine { line: number; email: string; fullName: string; problems: string[]; userId: string | null }
export interface UserImportResult { committed: boolean; rows: number; valid: number; created: number; lines: UserImportLine[]; refusal: string | null }

export function useImportUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, commit }: { file: File; commit: boolean }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('commit', commit ? 'true' : 'false');
      return (await apiClient.post<UserImportResult>('/api/v1/users/import', form)).data;
    },
    onSuccess: (r) => { if (r.committed) void qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
  });
}

export async function downloadUserImportTemplate() {
  const res = await apiClient.get<Blob>('/api/v1/users/import/template', { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'user-import-template.csv';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

// ---- user groups (ToR 4.1.1)

export interface UserGroupMember { userId: string; userName: string; addedAt: string; addedBy: string | null }
export interface UserGroup { id: string; name: string; description: string | null; isActive: boolean; memberCount: number; members: UserGroupMember[] }
export interface UserGroupInput { name: string; description?: string | null; isActive?: boolean }

export function useUserGroups(includeInactive = true) {
  return useQuery({ queryKey: ['admin', 'user-groups', includeInactive], queryFn: async () => (await apiClient.get<UserGroup[]>('/api/v1/user-groups', { params: { includeInactive } })).data });
}

function useGroupMutation<TIn, TOut>(fn: (input: TIn) => Promise<TOut>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user-groups'] }) });
}

export const useSaveUserGroup = () =>
  useGroupMutation(async ({ id, input }: { id?: string; input: UserGroupInput }) =>
    id ? (await apiClient.put<UserGroup>(`/api/v1/user-groups/${id}`, input)).data : (await apiClient.post<UserGroup>('/api/v1/user-groups', input)).data);

export const useDeleteUserGroup = () => useGroupMutation(async (id: string) => (await apiClient.delete(`/api/v1/user-groups/${id}`)).data);

export const useSetUserGroupMembers = () =>
  useGroupMutation(async ({ id, userIds }: { id: string; userIds: string[] }) => (await apiClient.put<UserGroup>(`/api/v1/user-groups/${id}/members`, userIds)).data);

// ---- the licence (ToR 1.0(xx))

export interface LicenceSummary { licensedUsers: number | null; activeUsers: number; remaining: number | null; expiresOn: string | null; daysToExpiry: number | null; expired: boolean; note: string | null; organisationName: string }

export function useLicence() {
  return useQuery({ queryKey: ['admin', 'licence'], queryFn: async () => (await apiClient.get<LicenceSummary>('/api/v1/settings/licence')).data, staleTime: 60_000 });
}
